import { computed, ref, type ComputedRef } from 'vue';

interface TokenState {
  token: string | null;
  sessionId: string | null;
  isLoading: boolean;
  error: string | null;
  claims: Record<string, unknown>;
  allowedOrigins: string[];
}

const state = ref<TokenState>({
  token: null,
  sessionId: null,
  isLoading: true,
  error: null,
  claims: {},
  allowedOrigins: []
});

let initialized = false;

// Captured once we know who framed us, so error states can postMessage a typed
// 'kaunt-error' back to the partner. Stays null in direct-link mode (no parent).
let parentOrigin: string | null = null;

// Decodes the JWT payload WITHOUT verifying the signature. Safe because every BFF
// call validates the signature server-side; we only read claims locally for routing
// and origin checks.
function parseJwt(token: string): Record<string, unknown> {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
}

function readAllowedOrigins(claims: Record<string, unknown>): string[] {
  const raw = claims['allowedOrigins'];
  if (Array.isArray(raw)) {
    return raw.filter((v): v is string => typeof v === 'string');
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
    } catch {
      return [];
    }
  }
  return [];
}

function applyToken(token: string) {
  const claims = parseJwt(token);
  const origins = readAllowedOrigins(claims);
  state.value.token = token;
  state.value.claims = claims;
  state.value.allowedOrigins = origins;
  state.value.isLoading = false;
  state.value.error = null;
}

function fail(message: string, code: string = 'error') {
  state.value.isLoading = false;
  state.value.error = message;
  notifyParentOfError(code);
}

function notifyParentOfError(code: string) {
  if (!parentOrigin) return;
  try {
    window.parent.postMessage(
      { type: 'kaunt-error', code, sessionId: state.value.sessionId },
      parentOrigin
    );
  } catch {
    // Best-effort. Parent may have closed or postMessage may be blocked; we've
    // already surfaced the error in the iframe UI either way.
  }
}

async function fetchTokenFromServer(sessionId: string) {
  try {
    const response = await fetch(`/api/sessions/${sessionId}/token`);
    if (response.status === 410 || response.status === 404) {
      fail('This link has already been used or has expired. Please return to your application to start a new session.', 'session-unavailable');
      return;
    }
    if (!response.ok) {
      fail(`Token fetch failed: ${response.status}`, 'token-fetch-failed');
      return;
    }
    const data = await response.json();
    if (typeof data.token !== 'string') {
      fail('Malformed token response', 'malformed-response');
      return;
    }
    // Strip the sessionId from the URL: the session is now consumed and the URL is no
    // longer useful as a credential. Keeping it would risk leaks via bookmarks, browser
    // history, or screen-sharing. Refresh from this point shows the "link consumed"
    // message instead of re-attempting a doomed fetch.
    history.replaceState({}, '', location.pathname);
    applyToken(data.token);
  } catch (err) {
    fail(err instanceof Error ? err.message : 'Failed to fetch token', 'token-fetch-failed');
  }
}

function startIframeHandshake(sessionId: string) {
  parentOrigin = (() => {
    try {
      return document.referrer ? new URL(document.referrer).origin : null;
    } catch {
      return null;
    }
  })();

  if (!parentOrigin) {
    fail('Cannot determine parent origin (referrer missing)', 'no-referrer');
    return;
  }

  const handleMessage = (event: MessageEvent) => {
    if (event.data?.type !== 'kaunt-token' || typeof event.data?.token !== 'string') return;

    const claims = parseJwt(event.data.token);
    const allowedOrigins = readAllowedOrigins(claims);

    if (!allowedOrigins.includes(event.origin)) {
      console.warn('[Kaunt] Discarding token from disallowed origin', event.origin);
      return;
    }
    if (claims['sessionId'] !== sessionId) {
      console.warn('[Kaunt] Discarding token with mismatched sessionId');
      return;
    }

    window.clearTimeout(timeoutId);
    window.removeEventListener('message', handleMessage);
    applyToken(event.data.token);
  };

  const timeoutId = window.setTimeout(() => {
    if (!state.value.token) {
      window.removeEventListener('message', handleMessage);
      fail('Token handshake timeout', 'handshake-timeout');
    }
  }, 5000);

  window.addEventListener('message', handleMessage);
  window.parent.postMessage({ type: 'kaunt-ready', sessionId }, parentOrigin);
}

function init() {
  if (initialized) return;
  initialized = true;

  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('sessionId');
  const mode = params.get('mode');

  if (!sessionId) {
    fail('This link has already been used or has expired. Please return to your application to start a new session.', 'missing-session');
    return;
  }
  state.value.sessionId = sessionId;

  // The mode is set by the Partner API when the URL is minted. There is no fallback —
  // missing or unknown mode is a hard error. Iframe-mode sessions cannot be opened
  // directly; direct-link sessions cannot be embedded.
  if (mode === 'iframe') {
    startIframeHandshake(sessionId);
  } else if (mode === 'direct-link') {
    fetchTokenFromServer(sessionId);
  } else {
    fail(`Missing or unknown mode in URL: ${mode ?? '(none)'}`, 'invalid-mode');
  }
}

export interface UseTokenResult {
  token: ComputedRef<string | null>;
  sessionId: ComputedRef<string | null>;
  isLoading: ComputedRef<boolean>;
  error: ComputedRef<string | null>;
  claims: ComputedRef<Record<string, unknown>>;
  allowedOrigins: ComputedRef<string[]>;
  getToken: () => string | null;
  getClaims: () => Record<string, unknown>;
  getAllowedOrigins: () => string[];
}

export function useToken(): UseTokenResult {
  init();

  return {
    token: computed(() => state.value.token),
    sessionId: computed(() => state.value.sessionId),
    isLoading: computed(() => state.value.isLoading),
    error: computed(() => state.value.error),
    claims: computed(() => state.value.claims),
    allowedOrigins: computed(() => state.value.allowedOrigins),
    getToken: () => state.value.token,
    getClaims: () => state.value.claims,
    getAllowedOrigins: () => state.value.allowedOrigins
  };
}

/**
 * Marks the current session as no longer usable. Intended for 401s from the
 * BFF: the JWT remains cryptographically valid (so the SPA accepted it on
 * boot), but the session it points at has been revoked or expired in Mongo,
 * so any further API call will keep failing. Surfaces the error in the SPA
 * shell and notifies the parent so partners can react. Idempotent — repeated
 * 401s after the first won't spam the parent with messages.
 */
export function markSessionInvalid(message: string, code: string = 'session-revoked'): void {
  if (state.value.error) return;
  fail(message, code);
}

export function useClaimValue<T>(claimName: string, defaultValue: T): T {
  const value = state.value.claims[claimName];
  if (value === undefined) return defaultValue;
  if (typeof value === 'string') {
    try { return JSON.parse(value) as T; } catch { return value as unknown as T; }
  }
  return value as T;
}
